import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, MapPin, Clock, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PeakMark } from "@/components/PeakMark";
import { useSeo } from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", organization: "", message: "" };

export default function ContactPage() {
  useSeo({
    title: "Contact & Work With Me | Smith Strategic Group",
    description:
      "Start the conversation with Smith Strategic Group. Tell Brady where you are and what you're building — most engagements begin with a short introductory call.",
    path: "/contact",
  });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      setForm(initial);
      toast.success("Thanks — Brady will be in touch soon.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App font-body antialiased text-neutral-950 bg-white">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="contactpage-hero"
        className="relative bg-[#0A0A0A] text-white pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden ssg-grain"
      >
        <div
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="contactpage-back"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={14} strokeWidth={1.6} />
            Back to Smith Strategic Group
          </Link>
          <div className="flex items-center gap-4 mb-8">
            <PeakMark size={30} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              Work With Me
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="contactpage-headline"
            className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-[88px] font-black tracking-tight leading-[1.0]"
          >
            Start the<br />
            <span className="italic font-light">conversation</span>
            <span className="text-[#E25555]">.</span>
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-neutral-300 leading-relaxed font-medium">
            Tell me a little about where you are, what you&apos;re building,
            and what you&apos;re looking for. Most engagements begin with a
            short introductory call to see if SSG is the right fit.
          </p>
        </div>
      </section>

      {/* FORM + SIDE INFO */}
      <section
        id="form"
        data-testid="contactpage-form-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* SIDE INFO */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-6">
                <span className="ssg-hero-rule" />
                <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
                  What to expect
                </span>
              </div>
              <h2
                data-testid="contactpage-expect-heading"
                className="font-display text-3xl md:text-4xl font-black tracking-tight text-neutral-950 leading-tight"
              >
                Every note is read<br />
                <span className="italic font-light">by me, personally</span>.
              </h2>

              <div className="mt-10 space-y-6">
                <ExpectRow
                  icon={Clock}
                  label="Response time"
                  body="Most messages get a reply within 24 hours on weekdays."
                />
                <ExpectRow
                  icon={ShieldCheck}
                  label="Privacy"
                  body="Nothing you share is added to a list, forwarded, or automated. This is a conversation, not a funnel."
                />
                <ExpectRow
                  icon={MapPin}
                  label="Location"
                  body="Remote across the United States. By introduction."
                />
              </div>

              <div className="mt-14 pt-10 border-t border-neutral-200">
                <p className="font-display italic text-2xl text-neutral-900 leading-snug">
                  &ldquo;The next version of you is closer than it feels.
                  Let&apos;s build the bridge.&rdquo;
                </p>
                <p className="mt-4 text-[12px] tracking-[0.24em] uppercase font-bold text-neutral-500">
                  — Brady Smith
                </p>
              </div>

              <div className="mt-10">
                <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-neutral-500 mb-3">
                  Applying for performance coaching?
                </div>
                <Link
                  to="/performance#apply"
                  data-testid="contactpage-perf-link"
                  className="inline-flex items-center gap-2 text-[13px] tracking-wider uppercase font-bold text-neutral-950 ssg-link-underline"
                >
                  Use the coaching application →
                </Link>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            {sent ? (
              <div
                data-testid="contactpage-success"
                className="border border-neutral-200 p-10 md:p-14"
              >
                <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
                  Message Received
                </div>
                <h3 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tight">
                  Thank you.
                </h3>
                <p className="mt-5 text-neutral-600 leading-relaxed font-medium">
                  Your note is in. I read every inquiry personally and will be
                  in touch shortly to schedule an introductory conversation.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contactpage-send-another"
                  className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-900"
                >
                  Send another note
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contactpage-form"
                className="space-y-10"
              >
                <div className="grid sm:grid-cols-2 gap-10">
                  <Field
                    label="Name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    testid="contactpage-input-name"
                    placeholder="Your full name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    testid="contactpage-input-email"
                    placeholder="you@company.com"
                  />
                </div>
                <Field
                  label="Organization"
                  value={form.organization}
                  onChange={update("organization")}
                  testid="contactpage-input-organization"
                  placeholder="Company, nonprofit, or independent"
                />
                <FieldArea
                  label="What are you looking for help with?"
                  required
                  value={form.message}
                  onChange={update("message")}
                  testid="contactpage-input-message"
                  placeholder="A few sentences on where you are and what you're navigating."
                  rows={6}
                />

                <div className="flex items-center justify-between flex-wrap gap-6 pt-4">
                  <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-400 max-w-xs">
                    Your note is read personally. Nothing shared. No lists.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contactpage-submit"
                    className="group inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#9B1C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                  >
                    {loading ? "Sending…" : "Start the Conversation"}
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.6}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const ExpectRow = ({ icon: Icon, label, body }) => (
  <div className="flex items-start gap-4">
    <div className="relative flex items-center justify-center w-12 h-12 flex-none">
      <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/40" />
      <span className="absolute inset-1.5 rounded-full bg-[#9B1C1C]/8" />
      <Icon size={20} strokeWidth={1.6} className="text-neutral-900 relative" />
    </div>
    <div>
      <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-1">
        {label}
      </div>
      <p className="text-[15px] text-neutral-700 leading-relaxed font-medium">
        {body}
      </p>
    </div>
  </div>
);

const Field = ({ label, required, value, onChange, type = "text", placeholder, testid }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
      {label} {required && <span className="text-[#9B1C1C]">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-lg text-neutral-950 placeholder:text-neutral-400 transition-colors"
    />
  </label>
);

const FieldArea = ({ label, required, value, onChange, placeholder, testid, rows = 4 }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
      {label} {required && <span className="text-[#9B1C1C]">*</span>}
    </span>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={rows}
      data-testid={testid}
      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-lg text-neutral-950 placeholder:text-neutral-400 resize-none transition-colors"
    />
  </label>
);
